import { ManagedAgentResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { stripMarkdown } from '@/lib/strip-markdown';

import { AgentDetailsPanelLink } from '@/components/RightSidebar/DM/AgentDetailsPanelLink';
import {
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

import { useAgentInformation } from '@/hooks/useAgentInformation';

import { AgentAvatar } from './AgentAvatar';
import { AgentCodeWriteBadge } from './AgentBadges/AgentCodeWriteBadge';
import { AgentInternetAccessBadge } from './AgentBadges/AgentInternetAccessBadge';
import { AgentRlsBadge } from './AgentBadges/AgentRlsBadge';
import { AgentSourcesBadges } from './AgentBadges/AgentSourcesBadges';

const TOGGLE_KEYS = ['Enter', ' '];

interface AgentInfoSubmenuProps {
    agent: ManagedAgentResponseModel;
    isSelected: boolean;
    onToggle: () => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function AgentInfoSubmenu({
    agent,
    isSelected,
    onToggle,
    open,
    onOpenChange
}: AgentInfoSubmenuProps) {
    const {
        load: loadAgentInfo,
        loading: loadingSources,
        mcpServerNames,
        description,
        hasInternetAccess,
        hasCodeWriteAccess
    } = useAgentInformation(agent);

    return (
        <DropdownMenuSub
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen) loadAgentInfo();
                onOpenChange(nextOpen);
            }}
        >
            <DropdownMenuSubTrigger
                hideChevron
                onClick={(event) => {
                    // Radix focuses the trigger here, and its close-on-focus-outside depends on it
                    event.currentTarget.focus();
                    event.preventDefault();
                    onToggle();
                }}
                onKeyDown={(event) => {
                    if (!TOGGLE_KEYS.includes(event.key)) return;
                    event.preventDefault();
                    onToggle();
                }}
                className="justify-between"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <AgentAvatar agentId={agent.agentId} className="size-6" />
                    <span className="truncate">
                        {agent.displayName}
                    </span>
                </div>
                <Switch
                    checked={isSelected}
                    className="pointer-events-none shrink-0"
                    size="sm"
                    tabIndex={-1}
                />
            </DropdownMenuSubTrigger>

            <DropdownMenuSubContent
                sideOffset={8}
                collisionPadding={8}
                className="flex flex-col gap-3 bg-background shadow-md p-4 w-80"
            >
                <div className="flex items-start gap-2">
                    <AgentAvatar
                        agentId={agent.agentId}
                        className="size-5"
                        animate={true}
                    />
                    <span className="font-medium text-sm wrap-break-word leading-5">
                        {agent.displayName}
                    </span>
                </div>
                <Separator />
                <p className="text-muted-foreground text-sm line-clamp-5 leading-5">
                    {stripMarkdown(description)}
                </p>
                {(hasInternetAccess ||
                    hasCodeWriteAccess ||
                    agent.enableRls) && (
                    <div className="flex flex-wrap gap-1.5">
                        <AgentInternetAccessBadge
                            hasInternetAccess={hasInternetAccess}
                        />
                        <AgentCodeWriteBadge
                            hasCodeWriteAccess={hasCodeWriteAccess}
                        />
                        <AgentRlsBadge hasRls={!!agent.enableRls} />
                    </div>
                )}
                <Separator />
                <div className="flex flex-col gap-1.5">
                    <AgentSourcesBadges
                        loading={loadingSources}
                        mcpServerNames={mcpServerNames}
                    />
                    <AgentDetailsPanelLink agentId={agent.agentId} />
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}
